import React, { useMemo, useState } from "react";
import { profileModel } from "./profileModel.mjs";
import { profileGroups, editSpec, editWrite } from "./profileView.mjs";
import { Proposed } from "./lead/parts";

/*
 * PROFILE: "Everything we know" (frame 1f), the tab beside the timeline, and the
 * one place on the record where repetition is allowed (the brief, "Who owns what").
 *
 * Every stored fact in groups (Person · {name}, Company · {name}, How they
 * arrived, What they told us, Deal and money, Relationship history, LinkedIn ads,
 * HubSpot export (frozen), Other), from the registry in record/profileModel.mjs,
 * never a dump of HubSpot's keys. Each row is label, value, source chip, pencil.
 * The chip is inferred from how the value arrived (reality.txt [11]); the pencil
 * is there only where a write path exists, and edits IN PLACE: the row opens into
 * its box with a black Save and a Cancel, the box carries a black focus ring (a
 * yellow one would be a second yellow on the screen, reality.txt [27]), and the
 * hint under it says what the edit changes ("Fit counts funding for up to 24
 * points, so “Would score” updates"). How each field writes is
 * record/profileView.mjs editWrite: PATCH for a column, properties_set for a
 * property, POST /contacts/:id/emails for another address.
 *
 * A group with nothing in it is one line ("HubSpot export (frozen) · none, arrived
 * after we left HubSpot"), and so is a group whose rows its line already says
 * (profileView.mjs profileGroups). The empty fields wait behind "Show empty (n)";
 * the search ("Search 31 facts") reads labels and values. Per-field source and
 * date is a Proposed tag: nothing stores it yet.
 *
 * TabsHead lives here (RecordTimeline re-exports it) so the timeline can hold the
 * profile's body in the phone's sheet without the two files importing each other.
 *
 * Props:
 *   record   the shell's bundle: contact, company, act, timeline.rows, ui.tab
 *   ops      ops.patchContact, ops.patchCompany, ops.setProps, ops.call,
 *            ops.reload, ops.setTab
 */

/**
 * Timeline · Profile, the tabs over both, with `right` at the far end (the
 * timeline's Add note). `tab` and `onTab` override the shell's tab, for the
 * phone's sheet, which keeps its own.
 */
export function TabsHead({ record, ops, tab = null, onTab = null, right = null }) {
  const current = tab || (record && record.ui && record.ui.tab) || "timeline";
  const go = onTab || ((t) => ops.setTab(t));
  const one = (id, label) => (
    <button
      type="button"
      role="tab"
      aria-selected={current === id}
      className={`rec-tab${current === id ? " is-on" : ""}`}
      onClick={() => go(id)}
    >
      {label}
    </button>
  );
  return (
    <div className="rec-tabs" role="tablist">
      {one("timeline", "Timeline")}
      {one("profile", "Profile")}
      <span className="rec-tabs__spacer" />
      {right}
    </div>
  );
}

// The stored value as the row shows it: a link, a logo, or the words.
function Value({ row }) {
  if (row.empty) return <span className="rec-pf__empty">not recorded</span>;
  if (row.key === "logo" && /^https?:/i.test(row.value)) {
    return <img className="rec-pf__logo" src={row.value} alt="" />;
  }
  if (row.link) {
    const href = /^https?:/i.test(row.link) ? row.link : `https://${row.link}`;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">{row.value} ↗</a>
    );
  }
  return <span className="rec-pf__value">{row.value}</span>;
}

// One field, opened for editing: the box, a black Save, Cancel, and what the
// edit changes. Enter saves a one-line box; a textarea keeps Enter for new lines.
export function EditRow({ row, spec, busy, error, errorAction, onSave, onCancel }) {
  const [v, setV] = useState(spec.initial || "");
  const save = () => onSave(v);
  const keys = (e) => {
    if (e.key === "Enter" && spec.input !== "textarea") {
      e.preventDefault();
      save();
    }
  };
  let box;
  if (spec.input === "select") {
    box = (
      <select className="rec-pf__input" autoFocus value={v} onChange={(e) => setV(e.target.value)} aria-label={row.label}>
        {spec.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  } else if (spec.input === "textarea") {
    box = <textarea className="rec-pf__input rec-pf__input--area" autoFocus rows={3} value={v} placeholder={spec.placeholder || ""} onChange={(e) => setV(e.target.value)} aria-label={row.label} />;
  } else {
    box = (
      <input
        className="rec-pf__input"
        autoFocus
        type={spec.input === "date" ? "date" : "text"}
        value={v}
        placeholder={spec.placeholder || ""}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={keys}
        aria-label={row.label}
      />
    );
  }
  return (
    <div className="rec-pf__edit">
      <span className="rec-pf__label">{row.label}</span>
      <div className="rec-pf__editbox">
        <div className="rec-pf__editline">
          {box}
          <button type="button" className="rec-btn rec-btn--dark" disabled={busy} onClick={save}>{busy ? "Saving…" : spec.add ? "Add" : "Save"}</button>
          <button type="button" className="rec-btn rec-btn--plain" disabled={busy} onClick={onCancel}>Cancel</button>
        </div>
        {error ? (
          <span className="rec-err">
            {error}
            {errorAction ? (
              <>
                {" "}
                <button type="button" className="rec-link" disabled={busy} onClick={errorAction.onClick}>{errorAction.label}</button>
              </>
            ) : null}
          </span>
        ) : spec.hint ? <span className="rec-pf__hint">{spec.hint}</span> : null}
      </div>
    </div>
  );
}

/** The tab's body under the tabs: search, the groups, the foot. */
export function ProfileBody({ record, ops }) {
  const [query, setQuery] = useState("");
  const [showEmpty, setShowEmpty] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [taken, setTaken] = useState(null);
  const [facets, setFacets] = useState(null);
  const { contact, company } = record;

  const m = useMemo(
    () =>
      profileModel({
        contact,
        company,
        act: record.act,
        timeline: record.timeline.rows,
        score: company && company.lead_score,
        query,
        showEmpty,
        // No situation: the rail's Deal and money points at the lead block instead
        // of repeating the fee, but Profile is the one place repetition is allowed,
        // so its money rows are always the full ones (frame 1f prints the fee).
        situation: null,
      }),
    [contact, company, record.act, record.timeline.rows, query, showEmpty]
  );
  const groups = useMemo(() => profileGroups(m.groups, { query }), [m.groups, query]);

  const open = (row) => {
    setError(null);
    setTaken(null);
    setEditing(row.key);
    // An industry is picked from the values the column already holds (the Leads
    // page's facets), never typed: the column holds enums like
    // COMPUTER_NETWORK_SECURITY, and a typed "Cybersecurity" would match nothing.
    if (row.edit && row.edit.field === "industry" && !facets) {
      ops.call("/contacts?stats=1&limit=1").then((r) => setFacets((r && r.facets) || { industry: [] })).catch(() => setFacets({ industry: [] }));
    }
  };
  const save = async (row, value, extra = {}) => {
    const w = editWrite(row, value, { contact, company });
    if (w.error) {
      setError(w.error);
      return;
    }
    setBusy(true);
    setError(null);
    setTaken(null);
    try {
      if (w.via === "contact") await ops.patchContact(w.id, { ...w.body, ...extra });
      else if (w.via === "company") await ops.patchCompany(w.body);
      else if (w.via === "props") await ops.setProps(w.target, w.id, w.props);
      else if (w.via === "email") {
        // A 409 says whose the address already is: "… is already on another contact".
        await ops.call(`/contacts/${w.id}/emails`, { method: "POST", body: JSON.stringify({ email: w.email }) });
        await ops.reload();
      }
      setEditing(null);
    } catch (e) {
      setError(e.message);
      // The primary email is another record's: offer to fold that record into
      // this one. The holder's id goes back with the save, so the server merges
      // only the record this message named.
      if (w.via === "contact" && w.body.email && e.body && e.body.reason === "belongs_to_another_contact") {
        setTaken({ holderId: e.body.contact_id, value });
      }
    } finally {
      setBusy(false);
    }
  };

  const rowEl = (r) => {
    const spec = editSpec(r, { contact, company, facets });
    if (editing === r.key && spec) {
      return (
        <EditRow
          key={`${r.key}-edit`}
          row={r}
          spec={spec}
          busy={busy}
          error={error}
          errorAction={
            taken
              ? { label: "Merge it into this record", onClick: () => save(r, taken.value, { merge_email_holder: taken.holderId }) }
              : null
          }
          onSave={(v) => save(r, v)}
          onCancel={() => {
            setEditing(null);
            setError(null);
            setTaken(null);
          }}
        />
      );
    }
    return (
      <div className="rec-pf__row" key={r.key}>
        <span className="rec-pf__label">{r.label}</span>
        <Value row={r} />
        {r.source ? <span className="rec-srcchip">{r.source.text}</span> : <span className="rec-pf__nochip" />}
        {spec ? (
          <button type="button" className="rec-pencil" aria-label={`${r.empty || spec.add ? "Add" : "Edit"} ${r.label.toLowerCase()}`} onClick={() => open(r)}>✎</button>
        ) : (
          <span />
        )}
      </div>
    );
  };

  let firstGroup = true;
  return (
    <div className="rec-pf">
      <label className="rec-search">
        <span className="rec-search__icon" aria-hidden="true">⌕</span>
        <input type="search" value={query} placeholder={m.searchLabel} onChange={(e) => setQuery(e.target.value)} aria-label={m.searchLabel} />
      </label>
      {groups.map((g) => {
        if (g.oneLine) {
          if (query.trim()) return null;
          return (
            <div className="rec-pf__line" key={g.id}>
              <span>
                <b>{g.title}</b>
                {g.summary ? <span className="rec-pf__summary"> · {g.summary}</span> : null}
              </span>
              {g.chip && <span className="rec-srcchip rec-pf__linechip">{g.chip.text}</span>}
            </div>
          );
        }
        const first = firstGroup;
        firstGroup = false;
        return (
          <React.Fragment key={g.id}>
            <div className={`rec-pf__group${first ? " is-first" : ""}`}>
              <span>{g.title}</span>
              <span className="rec-pf__count">{g.count}</span>
            </div>
            {g.rows.map(rowEl)}
          </React.Fragment>
        );
      })}
      {query.trim() && !groups.some((g) => !g.oneLine && g.rows.length) && (
        <div className="rec-pf__line"><span className="rec-pf__summary">No fact matches “{query.trim()}”.</span></div>
      )}
      <div className="rec-pf__foot">
        {m.empty > 0 || showEmpty ? (
          <button type="button" className="rec-link" onClick={() => setShowEmpty((v) => !v)}>
            {showEmpty ? "Hide empty" : m.showEmptyLabel}
          </button>
        ) : (
          <span />
        )}
        <span className="rec-meta">
          <Proposed>Per-field source and date</Proposed>
        </span>
      </div>
    </div>
  );
}

export default function ProfileTab({ record, ops }) {
  return (
    <section className="rec-pftab" aria-label="Profile">
      <TabsHead record={record} ops={ops} />
      {/* Keyed on the person: a colleague opened in the same panel starts with a clean search and no edit open. */}
      <ProfileBody key={String((record.contact && record.contact.id) || "none")} record={record} ops={ops} />
    </section>
  );
}
